import React, { useState, useMemo, useEffect } from 'react';
import {
  TagIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  ClockIcon,
  FireIcon,
  StarIcon,
  DocumentTextIcon,
  FolderIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useDocumentStorage } from '../../hooks/useDocumentStorage';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';
import { useDocumentTags } from '../../hooks/useDocumentTags';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface TagCloudFilterProps {
  onFilterChange: (filters: {
    searchQuery: string;
    selectedTags: string[];
    selectedCategories: string[];
    selectedFolders: string[];
    dateRange: string;
    fileTypes: string[];
  }) => void;
  className?: string;
}

interface TagData {
  name: string;
  count: number;
  color: string;
  category: 'popular' | 'recent' | 'category' | 'folder' | 'filetype';
  icon?: React.ComponentType<any>;
}

// Helper functions - defined outside component to avoid hoisting issues
const getTagColor = (count: number, maxCount: number) => {
  const intensity = count / maxCount;
  if (intensity > 0.7) return '#dc2626'; // red-600
  if (intensity > 0.5) return '#ea580c'; // orange-600
  if (intensity > 0.3) return '#ca8a04'; // yellow-600
  if (intensity > 0.1) return '#16a34a'; // green-600
  return '#6b7280'; // gray-500
};

const getFileTypeColor = (fileType: string) => {
  const colors: Record<string, string> = {
    'image': '#10b981', // emerald
    'application': '#3b82f6', // blue
    'text': '#8b5cf6', // violet
    'video': '#f59e0b', // amber
    'audio': '#ef4444', // red
  };
  return colors[fileType] || '#6b7280';
};

const getTagSize = (count: number, maxCount: number) => {
  const intensity = count / maxCount;
  if (intensity > 0.7) return 'text-xl font-bold';
  if (intensity > 0.5) return 'text-lg font-semibold';
  if (intensity > 0.3) return 'text-base font-medium';
  return 'text-sm font-normal';
};

export default function TagCloudFilter({ onFilterChange, className = '' }: TagCloudFilterProps) {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'popular' | 'recent' | 'category' | 'folder' | 'filetype'>('all');
  const [newTag, setNewTag] = useState('');
  const [showAddTag, setShowAddTag] = useState(false);

  // Get data from hooks
  const { documents } = useDocumentStorage();
  const { folderTree } = useDocumentFolders();
  const { tags: documentTags, addTag: addDocumentTag } = useDocumentTags();

  // Generate tag cloud data from documents
  const tagCloudData = useMemo(() => {
    const tagCounts = new Map<string, number>();
    const categoryCounts = new Map<string, number>();
    const folderCounts = new Map<string, number>();
    const fileTypeCounts = new Map<string, number>();

    // Count tags, categories, folders, and file types
    documents.forEach(doc => {
      // Count tags
      doc.tags?.forEach(tag => {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      });

      // Count categories
      if (doc.category?.name) {
        categoryCounts.set(doc.category.name, (categoryCounts.get(doc.category.name) || 0) + 1);
      }

      // Count folders (simplified)
      if (doc.folderId) {
        folderCounts.set(doc.folderId, (folderCounts.get(doc.folderId) || 0) + 1);
      }

      // Count file types
      if (doc.fileType) {
        const fileType = doc.fileType.split('/')[0]; // e.g., 'image', 'application'
        fileTypeCounts.set(fileType, (fileTypeCounts.get(fileType) || 0) + 1);
      }
    });

    const tags: TagData[] = [];

    // Popular tags from documentTags hook (more efficient)
    documentTags
      .sort((a, b) => b.count - a.count)
      .slice(0, 15)
      .forEach((docTag) => {
        tags.push({
          name: docTag.name,
          count: docTag.count,
          color: docTag.color || getTagColor(docTag.count, documentTags.length > 0 ? Math.max(...documentTags.map(t => t.count)) : 1),
          category: 'popular',
          icon: TagIcon
        });
      });

    // Fallback: if no documentTags, use document-based counting
    if (documentTags.length === 0) {
      Array.from(tagCounts.entries())
        .sort(([,a], [,b]) => b - a)
        .slice(0, 15)
        .forEach(([tag, count]) => {
          tags.push({
            name: tag,
            count,
            color: getTagColor(count, tagCounts.size > 0 ? Math.max(...Array.from(tagCounts.values())) : 1),
            category: 'popular',
            icon: TagIcon
          });
        });
    }

    // Categories
    Array.from(categoryCounts.entries()).forEach(([category, count]) => {
      tags.push({
        name: category,
        count,
        color: '#3b82f6', // blue
        category: 'category',
        icon: DocumentTextIcon
      });
    });

    // Folders (simplified names)
    folderTree.slice(0, 8).forEach(folder => {
      tags.push({
        name: folder.name,
        count: folder.documentCount,
        color: folder.color || '#6b7280',
        category: 'folder',
        icon: FolderIcon
      });
    });

    // File types
    Array.from(fileTypeCounts.entries()).forEach(([fileType, count]) => {
      tags.push({
        name: fileType,
        count,
        color: getFileTypeColor(fileType),
        category: 'filetype'
      });
    });

    return tags;
  }, [documents, folderTree, documentTags]);


  // Filter tags based on active filter
  const filteredTags = useMemo(() => {
    let filtered = tagCloudData;
    
    if (activeFilter !== 'all') {
      filtered = filtered.filter(tag => tag.category === activeFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(tag => 
        tag.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered.sort((a, b) => b.count - a.count);
  }, [tagCloudData, activeFilter, searchQuery]);

  const maxCount = tagCloudData.length > 0 ? Math.max(...tagCloudData.map(t => t.count)) : 1;

  // Handle tag selection
  const handleTagClick = (tag: TagData) => {
    let newSelectedTags = [...selectedTags];
    let newSelectedCategories = [...selectedCategories];
    let newSelectedFolders = [...selectedFolders];
    let newSelectedFileTypes = [...selectedFileTypes];

    if (tag.category === 'popular') {
      if (selectedTags.includes(tag.name)) {
        newSelectedTags = selectedTags.filter(t => t !== tag.name);
      } else {
        newSelectedTags.push(tag.name);
      }
    } else if (tag.category === 'category') {
      if (selectedCategories.includes(tag.name)) {
        newSelectedCategories = selectedCategories.filter(c => c !== tag.name);
      } else {
        newSelectedCategories.push(tag.name);
      }
    } else if (tag.category === 'folder') {
      if (selectedFolders.includes(tag.name)) {
        newSelectedFolders = selectedFolders.filter(f => f !== tag.name);
      } else {
        newSelectedFolders.push(tag.name);
      }
    } else if (tag.category === 'filetype') {
      if (selectedFileTypes.includes(tag.name)) {
        newSelectedFileTypes = selectedFileTypes.filter(f => f !== tag.name);
      } else {
        newSelectedFileTypes.push(tag.name);
      }
    }

    setSelectedTags(newSelectedTags);
    setSelectedCategories(newSelectedCategories);
    setSelectedFolders(newSelectedFolders);
    setSelectedFileTypes(newSelectedFileTypes);

    // Notify parent component
    onFilterChange({
      searchQuery,
      selectedTags: newSelectedTags,
      selectedCategories: newSelectedCategories,
      selectedFolders: newSelectedFolders,
      dateRange,
      fileTypes: newSelectedFileTypes
    });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setSelectedCategories([]);
    setSelectedFolders([]);
    setSelectedFileTypes([]);
    setDateRange('all');
    
    onFilterChange({
      searchQuery: '',
      selectedTags: [],
      selectedCategories: [],
      selectedFolders: [],
      dateRange: 'all',
      fileTypes: []
    });
  };

  const isTagSelected = (tag: TagData) => {
    if (tag.category === 'popular') return selectedTags.includes(tag.name);
    if (tag.category === 'category') return selectedCategories.includes(tag.name);
    if (tag.category === 'folder') return selectedFolders.includes(tag.name);
    if (tag.category === 'filetype') return selectedFileTypes.includes(tag.name);
    return false;
  };

  const totalSelectedFilters = selectedTags.length + selectedCategories.length + selectedFolders.length + selectedFileTypes.length + (dateRange !== 'all' ? 1 : 0);

  // Auto-expand when filters are active
  useEffect(() => {
    if (totalSelectedFilters > 0 && !isExpanded) {
      setIsExpanded(true);
    }
  }, [totalSelectedFilters, isExpanded]);

  // Add new tag functionality
  const addNewTag = async () => {
    const tag = newTag.trim();
    if (!tag) {
      toast.error('Kérlek adj meg egy tag nevet!');
      return;
    }
    
    // Check if tag already exists in the system
    const existingTag = documentTags.find(t => t.name.toLowerCase() === tag.toLowerCase()) || 
                       tagCloudData.find(t => t.name.toLowerCase() === tag.toLowerCase());
    if (existingTag) {
      toast.error('Ez a tag már létezik!');
      return;
    }
    
    try {
      // Add to documentTags collection
      await addDocumentTag(tag);
      
      // Add to selected tags (this will make it appear in the filter)
      const newSelectedTags = [...selectedTags, tag];
      setSelectedTags(newSelectedTags);
      
      // Notify parent component
      onFilterChange({
        searchQuery,
        selectedTags: newSelectedTags,
        selectedCategories,
        selectedFolders,
        dateRange,
        fileTypes: selectedFileTypes
      });
      
      // Clear input and close
      setNewTag('');
      setShowAddTag(false);
      toast.success(`"${tag}" tag létrehozva és hozzáadva a szűrőkhöz!`);
    } catch (error) {
      console.error('Error adding new tag:', error);
      toast.error('Hiba történt a tag létrehozása során!');
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="px-4 py-2">
        {/* Compact Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center space-x-2">
              <SparklesIcon className={`h-4 w-4 transition-colors ${
                totalSelectedFilters > 0 
                  ? 'text-indigo-600 dark:text-indigo-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`} />
              <h3 className={`text-sm font-medium transition-colors ${
                totalSelectedFilters > 0
                  ? 'text-indigo-900 dark:text-indigo-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                Intelligens Szűrés
                {totalSelectedFilters > 0 && (
                  <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-400">
                    ({totalSelectedFilters} aktív)
                  </span>
                )}
              </h3>
            </div>
          </button>
          
          <div className="flex items-center space-x-2">
            {/* Add Tag Button */}
            <button
              onClick={() => setShowAddTag(!showAddTag)}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
              title="Új tag hozzáadása"
            >
              <TagIcon className="h-3 w-3 mr-1" />
              Tag+
            </button>
            
            {totalSelectedFilters > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Törlés
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-6 h-6 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              title={isExpanded ? 'Szűrők elrejtése' : 'Szűrők megjelenítése'}
            >
              <ChevronDownIcon 
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
              />
            </button>
          </div>
        </div>

        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-screen opacity-100 mt-3' : 'max-h-0 opacity-0'
        }`}>
          <div className="space-y-3">
            {/* Add Tag Input - Show when button is clicked */}
            {showAddTag && (
              <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <TagIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-500" />
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Új tag neve..."
                      className="w-full pl-8 pr-3 py-2 text-sm border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-blue-400 dark:placeholder-blue-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addNewTag();
                        }
                      }}
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={addNewTag}
                    className="px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Hozzáadás
                  </button>
                  <button
                    onClick={() => {
                      setShowAddTag(false);
                      setNewTag('');
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  Ez a tag hozzáadódik a szűrőkhöz és használható lesz a dokumentumok címkézésére.
                </p>
              </div>
            )}

            {/* Compact Search Bar */}
            <div className="search-bar relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  onFilterChange({
                    searchQuery: e.target.value,
                    selectedTags,
                    selectedCategories,
                    selectedFolders,
                    dateRange,
                    fileTypes: selectedFileTypes
                  });
                }}
                placeholder="Keresés..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Compact Filter Categories */}
            <div className="flex flex-wrap items-center gap-1">
              {[
                { key: 'all', label: 'Összes', icon: SparklesIcon, count: filteredTags.length },
                { key: 'popular', label: 'Címkék', icon: TagIcon, count: tagCloudData.filter(t => t.category === 'popular').length },
                { key: 'category', label: 'Kategória', icon: DocumentTextIcon, count: tagCloudData.filter(t => t.category === 'category').length },
                { key: 'folder', label: 'Mappa', icon: FolderIcon, count: tagCloudData.filter(t => t.category === 'folder').length },
                { key: 'filetype', label: 'Típus', icon: DocumentTextIcon, count: tagCloudData.filter(t => t.category === 'filetype').length }
              ].map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => setActiveFilter(key as any)}
                  className={`flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    activeFilter === key
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  <span>{label}</span>
                  {count > 0 && (
                    <span className={`ml-1 px-1 rounded text-[10px] ${
                      activeFilter === key
                        ? 'bg-indigo-200 dark:bg-indigo-800'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  onFilterChange({
                    searchQuery,
                    selectedTags,
                    selectedCategories,
                    selectedFolders,
                    dateRange: e.target.value,
                    fileTypes: selectedFileTypes
                  });
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">Minden időszak</option>
                <option value="today">Ma</option>
                <option value="week">Ez a hét</option>
                <option value="month">Ez a hónap</option>
                <option value="year">Ez az év</option>
              </select>
            </div>

            {/* Compact Tag Cloud */}
            <div className="tag-cloud bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {filteredTags.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-xs">
                  <TagIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
                  <p>Nincs találat</p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {filteredTags.slice(0, 30).map((tag) => {
                    const selected = isTagSelected(tag);
                    const Icon = tag.icon;
                    
                    return (
                      <button
                        key={`${tag.category}-${tag.name}`}
                        onClick={() => handleTagClick(tag)}
                        className={`
                          inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 hover:scale-105
                          ${selected
                            ? 'ring-1 ring-indigo-500'
                            : ''
                          }
                        `}
                        style={{
                          backgroundColor: selected ? tag.color : `${tag.color}15`,
                          color: selected ? 'white' : tag.color,
                          borderColor: tag.color,
                          borderWidth: '1px'
                        }}
                        title={`${tag.name} (${tag.count})`}
                      >
                        {Icon && <Icon className="h-3 w-3" />}
                        <span className="max-w-[100px] truncate">{tag.name}</span>
                        <span className={`text-xs ${selected ? 'text-white/80' : 'opacity-70'}`}>
                          {tag.count}
                        </span>
                        {selected && (
                          <XMarkIcon className="h-2 w-2 ml-1 opacity-80" />
                        )}
                      </button>
                    );
                  })}
                  {filteredTags.length > 30 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">
                      +{filteredTags.length - 30} további...
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Compact Active Filters */}
            {totalSelectedFilters > 0 && (
              <div className="flex items-center justify-between py-1 px-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                <div className="flex flex-wrap gap-1">
                  {[
                    ...selectedTags, 
                    ...selectedCategories, 
                    ...selectedFolders, 
                    ...selectedFileTypes,
                    ...(dateRange !== 'all' ? [`📅 ${
                      dateRange === 'today' ? 'Ma' :
                      dateRange === 'week' ? 'Ez a hét' :
                      dateRange === 'month' ? 'Ez a hónap' :
                      dateRange === 'year' ? 'Ez az év' : dateRange
                    }`] : [])
                  ].slice(0, 5).map((filter, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 rounded"
                    >
                      {filter}
                    </span>
                  ))}
                  {totalSelectedFilters > 5 && (
                    <span className="text-xs text-indigo-600 dark:text-indigo-400">
                      +{totalSelectedFilters - 5}
                    </span>
                  )}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors ml-2"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
